// session_model.js — pure helpers for the workout-session model.
//
// A session owns its exercise list ("slots"), snapshotted from the program day
// when it starts (or empty for a freestyle workout). Editing the workout —
// adding, swapping, reordering, adding warm-up/drop sets — changes only the
// session; the program is updated on finish (auto-progression) and, if the
// user chooses, with the structural changes too.
//
// Slot: {
//   id,            // === program pe.id for program slots (logged sets key on it as set.peId)
//   fromProgram,   // true when the slot came from the program day (and wasn't swapped)
//   exerciseId, name, group, unit, yt, alternates,
//   repMin, repMax, rest, targetWeight, rpeTarget,
//   rows: [{ type: 'warmup'|'normal'|'drop'|'failure'|'myo'|'restpause'|'cluster'|'mini', tech? }],
//                  ('mini' rows follow a myo / rest-pause / cluster row; tech names it)
//   supersetId,    // slots sharing an id are performed as a superset
// }
// Logged set: { peId: slot.id, exerciseId, setIdx: row index, type, w (lb), reps, rpe|null, ts }

import { prescriptionFor, weightIncrementFor, withSetCount } from './utils.jsx';
import { exerciseKind } from './analytics.js';

let _seq = 0;
export const slotId = () => 'se_' + Date.now().toString(36) + (++_seq).toString(36) + Math.random().toString(36).slice(2, 5);

export const SET_TYPES = {
  normal: { label: 'Working', short: '' },
  warmup: { label: 'Warm-up', short: 'W' },
  drop: { label: 'Drop set', short: 'D' },
  failure: { label: 'To failure', short: 'F' },
  myo: { label: 'Myo-reps', short: 'M' },
  restpause: { label: 'Rest-pause', short: 'RP' },
  cluster: { label: 'Cluster', short: 'C' },
  mini: { label: 'Mini-set', short: '+' },
};

// Intensity techniques: an activation row followed by linked mini-set rows.
//   minis    mini-sets added when the technique is chosen
//   miniRest seconds between blocks
export const TECHNIQUES = {
  myo: { label: 'Myo-reps', minis: 4, miniRest: 15, sub: 'Activation set near failure, then 3–5 rep mini-sets with ~15 s rest' },
  restpause: { label: 'Rest-pause', minis: 2, miniRest: 20, sub: 'To failure, rest ~20 s, go again, twice' },
  cluster: { label: 'Cluster set', minis: 3, miniRest: 20, sub: 'One heavy set split into short blocks with ~20 s between' },
};
export const isTechnique = (type) => !!TECHNIQUES[type];
// A row that counts as its own set (not a warm-up, not a mini-set).
export const isMainRow = (r) => !!r && r.type !== 'warmup' && r.type !== 'mini';
// Activation row a mini-set belongs to (nearest preceding non-mini row), or -1.
export function miniOwner(slot, rowIdx) {
  for (let i = rowIdx - 1; i >= 0; i--) if (slot.rows[i]?.type !== 'mini') return i;
  return -1;
}
// Number of the mini-set within its group (1-based), or 0.
export function miniNumber(slot, rowIdx) {
  if (slot.rows[rowIdx]?.type !== 'mini') return 0;
  let k = 0;
  for (let i = rowIdx; i >= 0 && slot.rows[i]?.type === 'mini'; i--) k++;
  return k;
}
// Mini-set rows to insert after an activation row of a technique.
export const miniRows = (tech, n = TECHNIQUES[tech]?.minis || 0) => Array.from({ length: n }, () => ({ type: 'mini', tech }));
// Indices of the mini rows that directly follow rowIdx.
export function minisAfter(slot, rowIdx) {
  const out = [];
  for (let i = rowIdx + 1; i < slot.rows.length && slot.rows[i]?.type === 'mini'; i++) out.push(i);
  return out;
}

export function slotFromProgramExercise(program, pe) {
  const rx = prescriptionFor(program, pe);
  const n = Math.max(1, rx.sets ?? pe.sets ?? 3);
  return {
    id: pe.id,
    fromProgram: true,
    exerciseId: pe.exerciseId,
    name: rx.name || pe.name,
    group: pe.group,
    unit: pe.unit || 'reps',
    yt: rx.yt || pe.yt || '',
    alternates: rx.alternates || pe.alternates || [],
    repMin: rx.repMin ?? pe.repMin ?? 8,
    repMax: rx.repMax ?? pe.repMax ?? 10,
    rest: rx.rest ?? pe.rest ?? 120,
    targetWeight: pe.targetWeight || 0,
    rpeTarget: rx.rpeLast ?? null,
    notes: rx.notes || pe.note || null,
    rows: Array.from({ length: n }, () => ({ type: 'normal' })),
    supersetId: pe.supersetId || null,
  };
}

export function slotFromExercise(ex, opts = {}) {
  const kind = exerciseKind(ex, opts.catalog);
  const isTimed = ex.unit === 'sec' || kind.track === 'time';
  const cardio = kind.track === 'distance-time';
  return {
    id: slotId(),
    fromProgram: false,
    exerciseId: ex.id,
    name: ex.name,
    group: ex.group || 'Other',
    unit: isTimed ? 'sec' : (ex.unit || 'reps'),
    ...(cardio ? { track: 'distance-time' } : {}),
    yt: ex.yt || '',
    alternates: [],
    repMin: opts.repMin ?? (isTimed ? 30 : 8),
    repMax: opts.repMax ?? (isTimed ? 60 : 12),
    rest: opts.rest ?? (cardio ? 60 : ex.mechanic === 'compound' ? 150 : 90),
    targetWeight: opts.targetWeight ?? 0,
    rpeTarget: null,
    notes: null,
    rows: Array.from({ length: opts.sets ?? (cardio ? 1 : 3) }, () => ({ type: 'normal' })),
    supersetId: null,
  };
}

// { load, track } for a live slot: a timed slot (unit 'sec') is always 'time';
// otherwise the slot's own track, else the exercise's.
export function slotKind(slot, ex, cat) {
  const k = exerciseKind(ex || { name: slot?.name, unit: slot?.unit }, cat);
  const track = slot?.unit === 'sec' ? 'time' : (slot?.track || (k.track === 'time' ? 'reps' : k.track));
  return { load: slot?.load || k.load, track };
}

// Slots for a session; sessions created before the slot model derive them from
// the program day so in-progress and historical sessions keep working.
export function sessionSlots(session, programs) {
  if (!session) return [];
  if (Array.isArray(session.exercises)) return session.exercises;
  const prog = (programs || []).find(p => p.id === session.programId);
  const day = prog?.days.find(d => d.id === session.dayId);
  if (!day) {
    // Imported/legacy session without a resolvable day: synthesize slots from logged sets.
    const byPe = {};
    for (const st of session.sets || []) {
      if (!byPe[st.peId]) byPe[st.peId] = { id: st.peId, fromProgram: false, exerciseId: st.exerciseId, name: st.performedName || st.exerciseName || 'Exercise', group: 'Other', unit: 'reps', yt: '', alternates: [], repMin: 8, repMax: 12, rest: 120, targetWeight: 0, rows: [], supersetId: null };
      const rows = byPe[st.peId].rows;
      while (rows.length <= st.setIdx) rows.push({ type: 'normal' });
      rows[st.setIdx] = { type: st.type || 'normal', ...(st.tech ? { tech: st.tech } : {}) };
    }
    return Object.values(byPe);
  }
  return day.exercises.map(pe => slotFromProgramExercise(prog, pe));
}

export const workingRows = (slot) => slot.rows.map((r, i) => ({ ...r, i })).filter(isMainRow);

// Index of a row among the slot's main rows (no warm-ups, no mini-sets), used
// to line up with last session's sets. -1 for warm-ups and mini-sets.
export function workIndexOf(slot, rowIdx) {
  let k = -1;
  for (let i = 0; i <= rowIdx && i < slot.rows.length; i++) if (isMainRow(slot.rows[i])) k++;
  return isMainRow(slot.rows[rowIdx]) ? k : -1;
}

export function setsForSlot(session, slot) {
  return (session.sets || []).filter(s => s.peId === slot.id);
}

export function slotDone(session, slot) {
  const logged = new Set(setsForSlot(session, slot).map(s => s.setIdx));
  return slot.rows.every((_, i) => logged.has(i));
}

// Next row to log in a slot (first unlogged), or -1.
export function nextRow(session, slot) {
  const logged = new Set(setsForSlot(session, slot).map(s => s.setIdx));
  for (let i = 0; i < slot.rows.length; i++) if (!logged.has(i)) return i;
  return -1;
}

// The single "do this now" focus: the first slot (in order) with an unlogged
// row — except inside a superset, where focus alternates between partners.
export function focusOf(session, slots, lastLogged) {
  if (lastLogged) {
    const cur = slots.find(s => s.id === lastLogged.peId);
    if (cur && cur.supersetId) {
      const group = slots.filter(s => s.supersetId === cur.supersetId);
      const i = group.findIndex(s => s.id === cur.id);
      for (let k = 1; k <= group.length; k++) {
        const s = group[(i + k) % group.length];
        const r = nextRow(session, s);
        if (r >= 0) return { slotId: s.id, row: r };
      }
    }
  }
  for (const s of slots) {
    const r = nextRow(session, s);
    if (r >= 0) return { slotId: s.id, row: r };
  }
  return null;
}

// Rest after logging a row: none between superset partners until the round ends;
// short rests after warm-ups and drop sets.
export function restAfter(session, slots, slot, row, prefs) {
  const type = slot.rows[row]?.type || 'normal';
  if (type === 'warmup') return 60;
  if (slot.rows[row + 1]?.type === 'drop') return 0;
  // Myo-reps / rest-pause / clusters: the short intra-set rest, then the full rest.
  const next = slot.rows[row + 1];
  if (next?.type === 'mini') return TECHNIQUES[next.tech]?.miniRest || 15;
  if (slot.supersetId) {
    const group = slots.filter(s => s.supersetId === slot.supersetId);
    const last = group[group.length - 1];
    if (last.id !== slot.id) return 0;
  }
  return slot.rest || prefs?.restDefault || 120;
}

// Warm-up ramp for a working weight (lb). Barbell lifts start with the empty bar.
export function warmupRamp(workW, { equipment, inc = 5, bar = 45 } = {}) {
  if (!(workW > 0)) return [];
  const round = (x) => Math.max(0, Math.round(x / inc) * inc);
  const steps = workW >= 225 ? [[0.4, 8], [0.6, 5], [0.75, 3], [0.85, 1]]
    : workW >= 135 ? [[0.45, 8], [0.65, 5], [0.85, 2]]
    : [[0.5, 8], [0.75, 4]];
  const out = [];
  if (equipment === 'Barbell' && workW > bar * 1.5) out.push({ w: bar, reps: 10 });
  for (const [p, reps] of steps) {
    const w = round(workW * p);
    if (w > (out[out.length - 1]?.w ?? 0) && w < workW) out.push({ w, reps });
  }
  return out;
}

// Plates per side to load `total` (lb or kg — whatever unit the numbers are in).
export function platesFor(total, { bar = 45, plates = [45, 35, 25, 10, 5, 2.5] } = {}) {
  let side = (total - bar) / 2;
  if (side < 0) return { perSide: [], remainder: total - bar, achievable: bar };
  const out = [];
  for (const p of plates) {
    while (side >= p - 1e-9) { out.push(p); side -= p; }
  }
  const loaded = bar + 2 * out.reduce((a, b) => a + b, 0);
  return { perSide: out, remainder: +(side * 2).toFixed(2), achievable: +loaded.toFixed(2) };
}

// Weight step in lb for the user's unit system: 5 lb / 2.5 lb, or 2.5 kg / 1.25 kg.
export function stepFor(slot, units) {
  const lbStep = weightIncrementFor(slot);
  if (units !== 'kg') return lbStep;
  return lbStep >= 5 ? 2.5 / 0.45359237 : 1.25 / 0.45359237;
}

// Double progression applied on finish, from what was ACTUALLY lifted.
// Returns { program, progressed: [{ peId, name, from, to, inc }] }.
//   - working weight = heaviest weight used across the slot's working sets
//   - all planned working sets done and every one ≥ repMax at that weight → +step
//   - otherwise the target becomes the weight actually used (never lower than what you lifted)
export function applyProgression(program, session, slots, units) {
  if (!program) return { program, progressed: [] };
  const progressed = [];
  const days = program.days.map(d => d.id !== session.dayId ? d : {
    ...d,
    exercises: d.exercises.map(pe => {
      const slot = slots.find(s => s.id === pe.id && s.fromProgram && s.exerciseId === pe.exerciseId);
      if (!slot) return pe;
      const work = setsForSlot(session, slot).filter(st => st.type !== 'warmup' && st.type !== 'drop' && st.type !== 'mini' && st.reps > 0);
      if (!work.length) return pe;
      const w = Math.max(...work.map(st => st.w || 0));
      const atW = work.filter(st => (st.w || 0) === w);
      const plannedWorking = slot.rows.filter(r => r.type === 'normal' || r.type === 'failure' || isTechnique(r.type)).length;
      const hitTop = work.length >= plannedWorking && atW.length === work.length && atW.every(st => st.reps >= slot.repMax);
      const step = stepFor(slot, units);
      const to = hitTop ? +(w + step).toFixed(2) : w;
      if (to === (pe.targetWeight || 0)) return pe;
      if (hitTop) progressed.push({ peId: pe.id, name: slot.name, from: w, to, inc: step });
      return { ...pe, targetWeight: to };
    }),
  });
  return { program: { ...program, days }, progressed };
}

// Did the user change the workout's structure versus the program day?
export function structureChanged(program, session, slots) {
  const day = program?.days.find(d => d.id === session.dayId);
  if (!day) return false;
  if (day.exercises.length !== slots.length) return true;
  return slots.some((s, i) => {
    const pe = day.exercises[i];
    if (!pe || pe.id !== s.id || pe.exerciseId !== s.exerciseId) return true;
    const rx = prescriptionFor(program, pe);
    return (rx.sets ?? pe.sets) !== s.rows.filter(isMainRow).length || (pe.supersetId || null) !== (s.supersetId || null);
  });
}

// Writes the session's exercise list back to the program day ("Update program").
// Set counts go through withSetCount so periodized programs update the active
// and future weeks rather than the inline default.
export function programWithSessionStructure(program, session, slots) {
  const counts = {};
  const days = program.days.map(d => {
    if (d.id !== session.dayId) return d;
    const old = Object.fromEntries(d.exercises.map(pe => [pe.id, pe]));
    return {
      ...d,
      exercises: slots.map(s => {
        const prev = old[s.id];
        counts[s.id] = Math.max(1, s.rows.filter(isMainRow).length);
        if (prev && prev.exerciseId === s.exerciseId) return { ...prev, supersetId: s.supersetId || null };
        return {
          id: s.id, exerciseId: s.exerciseId, name: s.name, group: s.group,
          unit: s.unit, sets: counts[s.id], repMin: s.repMin, repMax: s.repMax, targetWeight: s.targetWeight || 0,
          rest: s.rest, yt: s.yt || '', alternates: s.alternates || [], supersetId: s.supersetId || null,
        };
      }),
    };
  });
  let out = { ...program, days };
  for (const [peId, n] of Object.entries(counts)) out = withSetCount(out, session.dayId, peId, n);
  return out;
}

// A live session left open for more than 12 hours is "stale": it belongs to an
// earlier day and must not swallow today's sets.
export const STALE_MS = 12 * 3600 * 1000;
export function isStaleSession(session, now = Date.now()) {
  // A past workout being logged after the fact (retro) is never stale.
  return !!session && !session.completedAt && !session.retro && now - (session.startedAt || 0) > STALE_MS;
}
// When closing a stale session, end it shortly after its last logged set (not
// "now", which would record a multi-day workout and skew duration stats).
export function staleEndAt(session) {
  const last = Math.max(session.startedAt || 0, ...(session.sets || []).map(s => s.ts || 0));
  return last + 60 * 1000;
}
